/**
 * Text out of bytes, without `TextDecoder`: this package carries no DOM or
 * Node types, and a statement file's encoding is itself untrusted — a Turkish
 * bank's CSV arrives as UTF-8, as UTF-16 from Excel's "Unicode text", or as
 * Windows-1254.
 */

/** The UTF-8 reading of `bytes`, or null when they are not valid UTF-8. */
export function decodeUtf8Strict(bytes: Uint8Array): string | null {
  const out: string[] = [];
  let chunk: number[] = [];
  const flush = () => {
    if (chunk.length > 0) {
      out.push(String.fromCodePoint(...chunk));
      chunk = [];
    }
  };

  let index = 0;
  while (index < bytes.length) {
    const first = bytes[index] as number;
    let codePoint: number;
    let length: number;
    if (first < 0x80) {
      codePoint = first;
      length = 1;
    } else if (first >= 0xc2 && first <= 0xdf) {
      codePoint = first & 0x1f;
      length = 2;
    } else if (first >= 0xe0 && first <= 0xef) {
      codePoint = first & 0x0f;
      length = 3;
    } else if (first >= 0xf0 && first <= 0xf4) {
      codePoint = first & 0x07;
      length = 4;
    } else {
      return null;
    }
    if (index + length > bytes.length) return null;
    for (let offset = 1; offset < length; offset += 1) {
      const next = bytes[index + offset] as number;
      if ((next & 0xc0) !== 0x80) return null;
      codePoint = (codePoint << 6) | (next & 0x3f);
    }
    if (length === 3 && (codePoint < 0x800 || (codePoint >= 0xd800 && codePoint <= 0xdfff))) {
      return null;
    }
    if (length === 4 && (codePoint < 0x10000 || codePoint > 0x10ffff)) return null;

    chunk.push(codePoint);
    if (chunk.length >= 4096) flush();
    index += length;
  }
  flush();
  return out.join('');
}

/**
 * Windows-1254 differs from Latin-1 in 0x80–0x9F and in the six Turkish
 * letters; every other byte is its own code point.
 */
const WINDOWS_1254_HIGH: Record<number, number> = {
  128: 0x20ac,
  130: 0x201a,
  131: 0x0192,
  132: 0x201e,
  133: 0x2026,
  134: 0x2020,
  135: 0x2021,
  136: 0x02c6,
  137: 0x2030,
  138: 0x0160,
  139: 0x2039,
  140: 0x0152,
  145: 0x2018,
  146: 0x2019,
  147: 0x201c,
  148: 0x201d,
  149: 0x2022,
  150: 0x2013,
  151: 0x2014,
  152: 0x02dc,
  153: 0x2122,
  154: 0x0161,
  155: 0x203a,
  156: 0x0153,
  159: 0x0178,
  208: 0x011e,
  221: 0x0130,
  222: 0x015e,
  240: 0x011f,
  253: 0x0131,
  254: 0x015f,
};

export function decodeWindows1254(bytes: Uint8Array): string {
  const out: string[] = [];
  for (let start = 0; start < bytes.length; start += 4096) {
    const codes: number[] = [];
    for (const byte of bytes.subarray(start, start + 4096)) {
      codes.push(WINDOWS_1254_HIGH[byte] ?? byte);
    }
    out.push(String.fromCharCode(...codes));
  }
  return out.join('');
}

function decodeUtf16(bytes: Uint8Array, littleEndian: boolean): string {
  const out: string[] = [];
  const units: number[] = [];
  for (let index = 0; index + 1 < bytes.length; index += 2) {
    const low = bytes[index] as number;
    const high = bytes[index + 1] as number;
    units.push(littleEndian ? low | (high << 8) : (low << 8) | high);
    if (units.length >= 4096) {
      out.push(String.fromCharCode(...units));
      units.length = 0;
    }
  }
  out.push(String.fromCharCode(...units));
  return out.join('');
}

export type TextEncoding = 'utf-8' | 'utf-16le' | 'utf-16be' | 'windows-1254';

/**
 * A byte-order mark decides; without one, valid UTF-8 is UTF-8 and anything
 * else is read as Windows-1254, the code page a Turkish Excel saves in. The
 * mark itself is never part of the text.
 */
export function decodeText(bytes: Uint8Array): { text: string; encoding: TextEncoding } {
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    const text = decodeUtf8Strict(bytes.subarray(3));
    if (text !== null) return { text, encoding: 'utf-8' };
  }
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: decodeUtf16(bytes.subarray(2), true), encoding: 'utf-16le' };
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: decodeUtf16(bytes.subarray(2), false), encoding: 'utf-16be' };
  }
  const utf8 = decodeUtf8Strict(bytes);
  if (utf8 !== null) return { text: utf8, encoding: 'utf-8' };
  return { text: decodeWindows1254(bytes), encoding: 'windows-1254' };
}
